"use client";

import { useRef } from "react";

export function SegmentedControl<T extends string>({ legend, name, options, value, onChange }: { legend: string; name: string; options: readonly T[]; value: T; onChange: (value: T) => void }) {
  return <fieldset className="auth-field">
    <legend className="auth-label">{legend}</legend>
    <div className="auth-segmented">
      {options.map((option) => <label key={option} className={value === option ? "selected" : undefined}>
        <input type="radio" name={name} value={option} checked={value === option} onChange={() => onChange(option)} />
        <span>{option}</span>
      </label>)}
    </div>
  </fieldset>;
}

export function CodeInput({ value, onChange, label, autoFocus = true }: { value: string[]; onChange: (value: string[]) => void; label: string; autoFocus?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (index: number) => refs.current[Math.max(0, Math.min(value.length - 1, index))]?.focus();

  const update = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    const next = [...value];
    if (digits.length > 1) {
      // A pasted or autofilled code fills the boxes from the current position.
      digits.slice(0, value.length - index).split("").forEach((digit, offset) => { next[index + offset] = digit; });
      onChange(next);
      focus(index + digits.length);
      return;
    }
    next[index] = digits;
    onChange(next);
    if (digits) focus(index + 1);
  };

  const onKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !value[index] && index > 0) { event.preventDefault(); const next = [...value]; next[index - 1] = ""; onChange(next); focus(index - 1); }
    if (event.key === "ArrowLeft") { event.preventDefault(); focus(index - 1); }
    if (event.key === "ArrowRight") { event.preventDefault(); focus(index + 1); }
  };

  return <div className="auth-code" role="group" aria-label={label}>
    {value.map((digit, index) => <input
      key={index}
      ref={(element) => { refs.current[index] = element; }}
      className="auth-code-box"
      aria-label={`Digit ${index + 1}`}
      value={digit}
      onChange={(event) => update(index, event.target.value)}
      onKeyDown={(event) => onKeyDown(index, event)}
      onFocus={(event) => event.target.select()}
      inputMode="numeric"
      autoComplete={index === 0 ? "one-time-code" : "off"}
      autoFocus={autoFocus && index === 0}
    />)}
  </div>;
}
